// M004-F0634 §0a — the ONE-list billing model (pure logic, no React).
//
// §0a (2026-09-24) supersedes the three-column board: ONE read
// (GET /api/v1/orgs/:orgId/billing/items?view=need|ok|billed|prob), person
// groups with server-computed subtotals, bulk approve, and every admin word
// (status_text, price_basis, problem, history) computed SERVER-SIDE per
// Core ML001-D014 §5d.4.1 (owner-approved 2026-09-27; routes on Core main
// since 5ec27616; contract pin via Core !695, merged 2026-09-28 as
// 04b3f8f6). The client renders the server's words verbatim and NEVER
// re-derives badge text, fix text or a state machine of its own — the §6
// client-side vocabulary of the superseded board is retired with it.
//
// The item `id` IS the posting id (§5d.4.1: "one shared identifier"); the
// `version` is the optimistic-concurrency token (the live quote id) that
// every approve carries (DC-3b).

import { inlineStage } from '../ledgerMoves/stageInline';

export const BILLING_VIEWS = ['need', 'ok', 'billed', 'prob'];

// §0a.1 — the four filters, verbatim labels, mapping one-to-one to the
// API's views.
export const VIEW_LABELS = {
  need: 'Needs approval',
  ok: 'Approved',
  billed: 'Billed',
  prob: 'Problems',
};

// §0a.2 / §5d.4.1 on_hold — "billing hold is a state, not a silence": the
// same DC-1 banner text the superseded board carried, shown while the env
// dispatch gate holds approved items.
export const GATE_CLOSED_BANNER = 'Billing hold is on — approved items wait here until dispatch is enabled';

// §5 — a real 403 from the billing read renders exactly this — never on a
// failed permissions read (that is the hub's "Could not load your
// permissions").
export const BILLPIPE_FORBIDDEN_NOTICE = 'You do not have permission to review billing.';

// Honest per-view empty states. The superseded §3 texts named columns; the
// ONE list names views in the same voice.
export const viewEmptyState = (view) => {
  switch (view) {
    case 'need':
      return 'Nothing needs your approval right now.';
    case 'ok':
      return 'Nothing is approved and waiting.';
    case 'billed':
      return 'Nothing here yet.';
    case 'prob':
      return 'No problems to fix.';
    default:
      return '';
  }
};

// The tab honours ?view= from the /app/ledgerApprovals redirect; anything
// else falls back to the §0a.1 default view.
export const DEFAULT_VIEW = 'need';

export const viewFromParam = (param) => (
  BILLING_VIEWS.includes(param) ? param : DEFAULT_VIEW
);

// §0a.3 — tolerant ItemsRead normalizer. The wire is the bare ItemsRead
// (counts / on_hold / groups); the normalizer keeps the render honest if an
// envelope wraps it, and never invents a group.
export const normalizeItemsRead = (body) => {
  const source = body?.data ?? body ?? {};
  const groups = Array.isArray(source.groups) ? source.groups : [];
  const counts = source.counts && typeof source.counts === 'object' ? source.counts : {};
  return {
    counts: {
      need: Number(counts.need ?? 0),
      ok: Number(counts.ok ?? 0),
      billed: Number(counts.billed ?? 0),
      prob: Number(counts.prob ?? 0),
    },
    onHold: source.on_hold === true,
    groups,
  };
};

// §0a.1 — the row face: booking title · room · date/time · amount · price
// basis · status_text. Date/time is SERVER-rendered (date YYYY-MM-DD,
// start/end HH:mm WALL-CLOCK in the item's own `timezone`, §5d.4.1, Core #140)
// — the client joins the strings and NEVER shifts them to the browser's or
// UTC's clock (M004-F0703). When the item names a valid IANA zone the zone's
// abbreviation for that date is appended so the clock is named, never guessed
// ("(browser time)" is retired); a missing or invalid zone (an older Core)
// renders exactly as before.

// A valid IANA zone name or '' — never throws.
export const validZoneOf = (timezone) => {
  if (typeof timezone !== 'string' || !timezone) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: timezone }).resolvedOptions().timeZone ? timezone : '';
  } catch {
    return '';
  }
};

const shortZoneName = (instant, zone) => {
  try {
    const part = new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(instant).find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch {
    return '';
  }
};

// The zone abbreviation (BST, GMT, GMT+10 ...) in force in `item.timezone` on
// `item.date` (read at that wall-clock date's noon); '' without a valid zone.
export const zoneAbbreviationFor = (item) => {
  const zone = validZoneOf(item?.timezone);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item?.date || '');
  if (!zone || !m) return '';
  return shortZoneName(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12)), zone);
};

export const formatItemWhen = (item) => {
  if (!item?.date) return '';
  const window = item.end && item.end !== item.start ? `${item.start}–${item.end}` : item.start;
  const abbreviation = zoneAbbreviationFor(item);
  return `${item.date} · ${window}${abbreviation ? ` ${abbreviation}` : ''}`;
};

// A history entry's `at` is an ISO instant. Render it in the item's zone
// ("2026-09-28 23:30 BST"); an unparseable instant or a missing/invalid zone
// returns the raw value exactly as before.
export const formatHistoryAt = (at, timezone) => {
  const zone = validZoneOf(timezone);
  const instant = new Date(at);
  if (!at || !zone || Number.isNaN(instant.getTime())) return at || '';
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(instant).map((p) => [p.type, p.value]),
    );
    const zoneName = shortZoneName(instant, zone);
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}${zoneName ? ` ${zoneName}` : ''}`;
  } catch {
    return at;
  }
};

// §0a.1 — amounts from amount_minor + currency; never formatted client-side
// from a float the API did not send.
export const formatMinorAmount = (amountMinor, currency) => {
  if (amountMinor === undefined || amountMinor === null || amountMinor === '') return '';
  const major = Number(amountMinor) / 100;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(major);
  } catch {
    return `${major.toFixed(2)} ${currency || ''}`.trim();
  }
};

// §0a.1 — the group subtotal (count and total for the person's rows in the
// current view). The group itself carries no currency (§5d.4.1 ItemGroup);
// the items' shared currency formats it.
//
// M004-F0676: in the Billed view the subtotal is the COUNT ONLY — a settled
// total is the Ledger's, not Core's to restate (pass `settled: true`).
export const formatGroupSubtotal = (group, { settled = false } = {}) => {
  const items = Array.isArray(group?.items) ? group.items : [];
  const count = items.length;
  const label = `${count} item${count === 1 ? '' : 's'}`;
  if (settled) return label;
  const currency = items[0]?.currency;
  return `${label} · ${formatMinorAmount(group?.total_minor, currency)}`;
};

// M004-F0675 — a settled (handed-off) item. Read straight from the server's
// status; the client keeps no state machine. Settled items are READ-ONLY in
// Core: no amount, no edit control, whatever the can flags say.
export const isSettledItem = (item) => item?.status === 'billed';

// M004-F0716 (design 5.2 row 14): Core maps posted, handed_over and charged ALL to status 'billed', so "billed" is not "the Ledger
// holds it". The drawer says what Core's own `status_text` says, and adds "cannot be changed here" only for the two texts that
// mean the Ledger has it (Core's words: "Handed to the Ledger ..." / "Charged ..."). A "Sent to the Ledger - sending" row is not
// yet acknowledged, so no claim of a hand-off is made for it.
export const ledgerHasItText = (statusText) => /^(Handed to the Ledger|Charged)\b/.test(String(statusText || ''));

// M004-F0716 phase B (design 5.2 rows 12-18; Core #311): every billing item row carries `stage` (Spec TxStage), `stage_detail`,
// `next_action` (a sentence or null), `conditions[]` ({code, message, next_action}, empty when none), `hand_off_id`, `tx_id`, `kind`.
// The row, the drawer's status and its settled sentence are built from the STAGE (the shared stage->sentence map, one source:
// ledgerMoves/stageInline.js). Core's `status_text` is the DISPLAY FALLBACK only, for an older Core whose row has no stage (or a
// stage this build does not know): it never wins over a stage that is present.
export const LEDGER_HELD_STAGES = Object.freeze(['handed_off', 'advice', 'draft', 'issued', 'posted', 'paid', 'credited']);

const sentence = (value) => (typeof value === 'string' && value.trim() ? value.trim() : '');

export const billingStageView = (item) => {
  const stage = sentence(item?.stage);
  const detail = sentence(item?.stage_detail);
  const inline = stage ? inlineStage({ stage, detail, reason: detail, nextAction: item?.next_action, handOffId: item?.hand_off_id || '', txnId: item?.tx_id || '' }) : null;
  const conditions = (Array.isArray(item?.conditions) ? item.conditions : [])
    .filter((c) => c && typeof c === 'object' && (sentence(c.message) || sentence(c.code)))
    .map((c) => ({ code: sentence(c.code), message: sentence(c.message), nextAction: sentence(c.next_action) }));
  if (!inline) {
    return { source: 'status_text', stage: '', text: sentence(item?.status_text) ? item.status_text : '', nextAction: '', nextMissing: false, problem: false, sending: false, held: false, conditions };
  }
  return {
    source: 'stage',
    stage,
    text: inline.text,
    // Core's `next_action` verbatim; the fixed "Core did not say" sentence only for a failure stage that came without one.
    nextAction: inline.nextAction,
    nextMissing: inline.nextMissing,
    problem: inline.problem,
    sending: inline.sending,
    held: LEDGER_HELD_STAGES.includes(stage),
    conditions,
  };
};

// The sentence under a SETTLED (status 'billed') item. By stage when the row has one; "cannot be changed here" only for a stage the
// Ledger holds (never for sending_to_ledger / dispatch_failed). With no stage: Core's own words, as phase A.
export const settledSentence = (item) => {
  const view = billingStageView(item);
  const locked = view.source === 'stage' ? view.held : ledgerHasItText(item?.status_text);
  return `${view.text}${locked ? ' It cannot be changed here. A correction is made in the Ledger as a credit.' : ''}`;
};

// The Ledger's hand-off id when the item carries it (additive on Core's
// BillingItem; absent today — see the design doc §5).
export const handOffIdFor = (item) => (
  typeof item?.hand_off_id === 'string' && item.hand_off_id ? item.hand_off_id : ''
);

// Where a settled item is corrected: the organisation's Ledger members page
// (the wire names no single customer yet).
export const ledgerHrefFor = (organisationId) => (
  `/app/ledger/${encodeURIComponent(organisationId)}/members`
);

// §0a.2 — a Problems row states the fix inline: problem.text plus
// problem.fix rendered as an inline fix link. The merged surface's fix
// values are anchor tokens (booking-pricing-control, booking-teacher,
// studio-approve, retry-dispatch, ledger-entry, contact-operator,
// booking-settings) — resolved to the ONE reachable surfaces:
//   - drawer tokens: the item's drawer holds the pricing control and the
//     Retry action, so the link OPENS THE DRAWER (the action inside is
//     still gated by the server's can flags);
//   - studio tokens: the booking is fixed on the hub's Studio tab
//     (approve the booking, set its teacher) — a real URL entry
//     (/app/bookings?tab=studio&org=<id>);
//   - anything else: plain text (no link to nowhere).
export const fixTargetFor = (item) => {
  const fix = item?.problem?.fix || '';
  if (fix === 'booking-pricing-control' || fix === 'retry-dispatch') {
    return { kind: 'drawer', label: 'Fix this item' };
  }
  if (fix === 'studio-approve' || fix === 'booking-teacher') {
    return { kind: 'studio', label: 'Open the Studio tab' };
  }
  return { kind: 'text', label: '' };
};

export const studioTabHref = (organisationId) => (
  `/app/bookings?tab=studio${organisationId ? `&org=${encodeURIComponent(organisationId)}` : ''}`
);

// §0a.2 — errors render the API's admin-readable message verbatim; codes
// are visible ONLY under the drawer's collapsed "Details for support"
// disclosure. This builds that disclosure's lines: the one shared item id
// plus any raw codes the surface produced this session.
export const supportDetailLines = (item, errorCodes) => {
  const lines = [];
  if (item?.id !== undefined && item?.id !== null && item?.id !== '') {
    lines.push(`Item id: ${item.id}`);
  }
  const seen = new Set();
  (Array.isArray(errorCodes) ? errorCodes : []).forEach((code) => {
    if (code && !seen.has(code)) {
      seen.add(code);
      lines.push(`Code: ${code}`);
    }
  });
  return lines;
};

// §5d.4.1 — the 401 arm. The approved schema's Error is {code,message}
// ONLY (additionalProperties:false): the surface as merged carries NO
// step-up challenge on the wire. The drawer keeps the proven
// challenge-bearing replay for any Core that (additively) restores
// details.challenge, and until then renders the server's message verbatim
// — never a dialog it cannot satisfy. This predicate names the bare arm.
export const isBareStepUpRequired = (error) => (
  error?.response?.status === 401
  && error?.response?.data?.code === 'STEP_UP_REQUIRED'
  && !getStepUpChallengeFromErrorSafe(error)
);

// Local import-free mirror of utils/stepUp's challenge extraction, so the
// model stays dependency-free for jest; the drawer uses the real util.
const getStepUpChallengeFromErrorSafe = (error) => {
  const payload = error?.response?.data || {};
  return payload?.details?.challenge || null;
};

// The bulk-approve per-item outcome list (§5d.4.1 ApproveOutcome): a
// failure on one item never aborts the others. Returns the errors to render
// (message verbatim) and the codes for Details-for-support.
export const batchOutcomeErrors = (results) => {
  const list = Array.isArray(results) ? results : [];
  return list
    .filter((outcome) => outcome && outcome.error)
    .map((outcome) => ({
      id: outcome.id,
      message: outcome.error.message || 'The request failed.',
      code: outcome.error.code || '',
    }));
};
